import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Outlet, useLocation } from 'react-router';
import styled from 'styled-components';

import { Navigate, catchAll, useTypedParams } from '@sorare/routing';
import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { BottomPortal } from '@sorare/core/src/atoms/layout/BottomPortal';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import {
  ANY_SPORT_MY_CARDS,
  ANY_SPORT_MY_CARDS_CARDS,
  ANY_SPORT_MY_CARDS_CARDS_TYPE,
  ANY_SPORT_MY_CARDS_COLLECTIONS,
  ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE,
  ANY_SPORT_MY_CARDS_GALLERY,
  ANY_SPORT_MY_CARDS_VAULT,
  ANY_SPORT_MY_CARDS_VAULT_TYPE,
} from '@sorare/core/src/constants/__generated__/routes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useQueryString } from '@sorare/core/src/hooks/useQueryString';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { RequireAuth } from '@sorare/core/src/routing/RequireAuth';

import { CardOrCollectionRaritySwitcher } from '#src/components/myCards/CardOrCollectionRaritySwitcher/index.tsx';
import { NewRewardsBanner } from '#src/components/rewards/NewRewardsBanner/index.tsx';
import { LazyLastFixtureStory } from '#src/components/so5/LastFixtureStory/index.tsx';
import { MyCardsContext } from '#src/contexts/myCards/index.tsx';

import { MyCardsLayoutContext } from './MyCardsLayoutContext.tsx';
import type {
  MyCardsLayoutQuery,
  MyCardsLayoutQueryVariables,
} from './__generated__/index.graphql.ts';

const RewardsBannerWrapper = styled.div`
  padding: var(--double-unit);
`;

const MaxWidthHorizontal = styled(Horizontal)`
  max-width: 100%;
`;

type Props = {
  currentUser: MyCardsLayoutQuery['currentUser'];
  type: Nullable<Exclude<Rarity, typeof Rarity.custom_series>>;
};

const MyCardsLayoutComponent = ({ currentUser, type }: Props) => {
  const { generateSportContextPath, matchAnySportPath, sport } =
    useSportContext();
  const isLaptopAndAbove = useIsLaptop();
  const location = useLocation();
  const { lifecycle, update } = useLifecycle();
  const rewardsBannerRef = useRef<HTMLDivElement>(null);
  const { activeRevealableCardCampaigns } = useConfigContext();
  const { currentUser: contextCurrentUser } = useCurrentUserContext();
  const { unclaimedSo5Rewards = [] } = currentUser || {};
  const { cardCounts } = contextCurrentUser || {};

  const cardsCountByRarity: Record<
    Exclude<Rarity, typeof Rarity.custom_series>,
    number
  > = {
    [Rarity.common]: cardCounts?.common || 0,
    [Rarity.limited]: cardCounts?.limited || 0,
    [Rarity.rare]: cardCounts?.rare || 0,
    [Rarity.super_rare]: cardCounts?.superRare || 0,
    [Rarity.unique]: cardCounts?.unique || 0,
  };

  const revealableCardCampaigns = activeRevealableCardCampaigns ?? [];

  const cardEdition = useQueryString('cardEdition');
  const [displayedCardEdition, setDisplayedCardEdition] = useState<
    string | undefined
  >(cardEdition);

  if (
    revealableCardCampaigns &&
    displayedCardEdition === undefined &&
    revealableCardCampaigns[0]?.blueprintCardEditions[0]
  ) {
    setDisplayedCardEdition(
      revealableCardCampaigns[0]?.blueprintCardEditions[0]
    );
  }

  const selectedCampaignSlug =
    (displayedCardEdition !== undefined &&
      revealableCardCampaigns.find(c =>
        c.blueprintCardEditions.includes(displayedCardEdition)
      )?.slug) ||
    revealableCardCampaigns[0]?.slug;

  const matchCollectionsPage = matchAnySportPath(
    catchAll(ANY_SPORT_MY_CARDS_COLLECTIONS),
    location.pathname
  );

  const lastVisitedRarity =
    lifecycle?.[LIFECYCLE.lastVisitedMyCardsRarity]?.[sport];

  const defaultRarity =
    (lastVisitedRarity as Rarity) ||
    (Object.entries(cardsCountByRarity).find(
      ([, value]) => !!value
    )?.[0] as Rarity) ||
    Rarity.limited;

  const updateLastVisitedRarity = (clickedRarity: Rarity) => {
    update(LIFECYCLE.lastVisitedMyCardsRarity, {
      ...(lifecycle?.[LIFECYCLE.lastVisitedMyCardsRarity] || {}),
      [sport]: clickedRarity,
    });
  };

  const rewardsBannerHeight =
    rewardsBannerRef.current?.getBoundingClientRect().height;

  return (
    <>
      <div ref={rewardsBannerRef}>
        <NewRewardsBanner
          so5Rewards={unclaimedSo5Rewards}
          Wrapper={RewardsBannerWrapper}
        />
      </div>
      <MyCardsLayoutContext.Provider
        value={{
          selectedCampaignSlug,
          defaultRarity,
          rewardsBannerHeight,
        }}
      >
        <Outlet />
      </MyCardsLayoutContext.Provider>
      {!isLaptopAndAbove && (
        <BottomPortal>
          <MaxWidthHorizontal>
            <FullViewportScroll>
              {!!(
                matchAnySportPath(
                  catchAll(ANY_SPORT_MY_CARDS_CARDS),
                  location.pathname
                ) || matchCollectionsPage
              ) &&
                !!type && (
                  <CardOrCollectionRaritySwitcher
                    user={contextCurrentUser}
                    type={type}
                    rarityPath={newRarity => {
                      if (matchCollectionsPage) {
                        return generateSportContextPath(
                          ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE,
                          {
                            params: {
                              type: newRarity,
                            },
                          }
                        );
                      }
                      return generateSportContextPath(
                        ANY_SPORT_MY_CARDS_CARDS_TYPE,
                        {
                          params: {
                            type: newRarity,
                          },
                        }
                      );
                    }}
                    onClick={updateLastVisitedRarity}
                    forceShowCommon={
                      !!matchCollectionsPage &&
                      !!currentUser?.userCardCollectionsPresence
                    }
                  />
                )}
            </FullViewportScroll>
          </MaxWidthHorizontal>
        </BottomPortal>
      )}
    </>
  );
};

const MY_CARDS_LAYOUT_QUERY = gql`
  query MyCardsLayoutQuery($sport: Sport!) {
    so5 {
      myAvailableFixtureStory(sport: $sport) {
        id
        ...LazyLastFixtureStory_story
      }
    }
    currentUser {
      slug
      userCardCollectionsPresence(rarity: common, sport: $sport)
      unclaimedSo5Rewards(sport: $sport) {
        slug
        ...NewRewardsBanner_so5Reward
      }
    }
    cardSets(onlyWithOwnedCards: true) {
      nodes {
        slug
        name
      }
    }
  }
  ${NewRewardsBanner.fragments.so5Reward}
  ${LazyLastFixtureStory.fragments.story}
` as TypedDocumentNode<MyCardsLayoutQuery, MyCardsLayoutQueryVariables>;

const GalleryTabs = ({
  type,
}: {
  type: Nullable<Exclude<Rarity, typeof Rarity.custom_series>>;
}) => {
  const { generateSportContextPath } = useSportContext();
  const isLaptopAndAbove = useIsLaptop();
  const seriesNav = useSeriesNav();

  if (!isLaptopAndAbove) {
    return null;
  }
  return (
    <OnboardingHighlightWrapper highlightName="CollectionNavigationSwitch">
      <TabBar fullWidth variant="flat">
        <Tab
          to={
            type
              ? generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS_TYPE, {
                  params: { type },
                })
              : generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS)
          }
          matches={[catchAll(ANY_SPORT_MY_CARDS_CARDS)]}
        >
          <FormattedMessage {...navLabels.gallery} />
        </Tab>
        <Tab
          to={
            type
              ? generateSportContextPath(ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE, {
                  params: { type },
                })
              : generateSportContextPath(ANY_SPORT_MY_CARDS_COLLECTIONS)
          }
          matches={[
            catchAll(ANY_SPORT_MY_CARDS_COLLECTIONS),
            catchAll(ANY_SPORT_MY_CARDS_GALLERY),
          ]}
        >
          <FormattedMessage {...navLabels.collections} />
        </Tab>
        {!seriesNav && (
          <Tab
            to={
              type && type !== Rarity.common
                ? generateSportContextPath(ANY_SPORT_MY_CARDS_VAULT_TYPE, {
                    params: { type },
                  })
                : generateSportContextPath(ANY_SPORT_MY_CARDS_VAULT)
            }
          >
            <FormattedMessage {...navLabels.vault} />
          </Tab>
        )}
      </TabBar>
    </OnboardingHighlightWrapper>
  );
};

const isNullableOrRarityStr = (
  type: Nullable<string>
): type is Nullable<Exclude<Rarity, typeof Rarity.custom_series>> =>
  !type || (type in Rarity && type !== Rarity.custom_series);

export const MyCardsLayout = <
  Params extends {
    type?: string;
  },
>() => {
  const { type } = useTypedParams<Params>();
  const { generateSportContextPath } = useSportContext();
  const { data } = useQueryLoader<MyCardsLayoutQuery>();

  if (!isNullableOrRarityStr(type)) {
    return (
      <Navigate replace to={generateSportContextPath(ANY_SPORT_MY_CARDS)} />
    );
  }
  return (
    <RequireAuth>
      <MyCardsContext value={{ cardSets: data.cardSets?.nodes || [] }}>
        {!!data?.so5?.myAvailableFixtureStory && (
          <LazyLastFixtureStory story={data.so5.myAvailableFixtureStory} />
        )}
        <ContentWithStickyHeader header={<GalleryTabs type={type} />}>
          <MyCardsLayoutComponent currentUser={data.currentUser} type={type} />
        </ContentWithStickyHeader>
      </MyCardsContext>
    </RequireAuth>
  );
};

export const loader = queryLoader(MY_CARDS_LAYOUT_QUERY, ({ sport }) => ({
  sport,
}));
